import { useCallback, useState } from 'react'

import { TrainingCertificateDocumentIdsDocument } from 'generated/resources/graphql'

import { downloadBlob } from 'modules/core/lib/download'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import { apiClient } from 'modules/core/lib/mobx-rest'

import { BulkSelection } from 'modules/trainings/components/Revamp/TrainingsDetail/ParticipantsOneDataTable/hooks/useBulkActionsConfig'

const downloadStatus = {
  idle: 'idle',
  fetching: 'fetching',
  downloading: 'downloading',
  noCertificates: 'no_certificates',
  error: 'error',
} as const

type DownloadStatus = (typeof downloadStatus)[keyof typeof downloadStatus]

interface UseCertificateDownloadParams {
  trainingId: number
}

export const useCertificateDownload = ({ trainingId }: UseCertificateDownloadParams) => {
  const [status, setStatus] = useState<DownloadStatus>(downloadStatus.idle)
  const [documentIds, setDocumentIds] = useState<number[]>([])

  const { refetch } = useQuery(TrainingCertificateDocumentIdsDocument, {
    variables: {
      trainingId,
      excludedMembershipIds: [],
    },
    skip: true,
  })

  const fetchDocumentIds = useCallback(
    async (selection: BulkSelection): Promise<number[]> => {
      setStatus(downloadStatus.fetching)

      try {
        const isSelectAll = selection.type === 'all'
        const excludedMembershipIds = isSelectAll ? selection.excludedIds : undefined
        const filters = isSelectAll ? selection.filters : undefined
        const trainingMembershipIds = selection.type === 'manual' ? selection.ids : undefined

        const result = await refetch({
          trainingId: isSelectAll ? trainingId : undefined,
          excludedMembershipIds,
          trainingMembershipIds,
          classId: filters?.classId,
          dueDate: filters?.dueDate,
          search: filters?.search,
          status: filters?.status,
          teamId: filters?.teamId,
        })

        const ids =
          result.data?.trainings?.trainingMembershipFilesConnection?.nodes
            ?.map((node) => node.documentId)
            .filter((id): id is number => id != null) ?? []

        setDocumentIds(ids)

        if (ids.length === 0) {
          setStatus(downloadStatus.noCertificates)
        } else {
          setStatus(downloadStatus.idle)
        }

        return ids
      } catch {
        setStatus(downloadStatus.error)
        return []
      }
    },
    [refetch, trainingId]
  )

  const downloadDocuments = useCallback(
    async (ids?: number[]) => {
      const idsToDownload = ids ?? documentIds

      if (idsToDownload.length === 0) {
        setStatus(downloadStatus.noCertificates)
        return { success: false, reason: 'no_certificates' as const }
      }

      setStatus(downloadStatus.downloading)

      try {
        const response = await apiClient().post(
          '/documents/download_documents',
          { ids: idsToDownload.join(','), flat: true },
          { responseType: 'blob' }
        )

        if (!response || response.size === 0) {
          setStatus(downloadStatus.error)
          return { success: false, reason: 'error' as const }
        }

        const blob = new Blob([response], { type: 'application/zip' })
        downloadBlob(
          blob,
          `${i18n.t('trainings.trainings_revamp.training_detail.employees_table.certificates')}.zip`
        )
        setStatus(downloadStatus.idle)
        return { success: true }
      } catch {
        setStatus(downloadStatus.error)
        return { success: false, reason: 'error' as const }
      }
    },
    [documentIds]
  )

  return {
    fetchDocumentIds,
    downloadDocuments,
    documentIds,
    isFetching: status === downloadStatus.fetching,
    isDownloading: status === downloadStatus.downloading,
    hasNoCertificates: status === downloadStatus.noCertificates,
    hasError: status === downloadStatus.error,
  }
}
